import moduleInfo from '../module.json';
import type { AppRuntime } from '@ayocore/platform/client/runtime/AppRuntime';
import { lazy } from 'react';
import { IconFileImport, IconChartBar } from '@tabler/icons-react';
import { CrmReportsRegistry } from '@ayocore/erp/crm/client/components/Reports/CrmReportsRegistry';
import { registerGrasiasLeadViewExtensions } from './extensions/GrasiasLeadViewExtension';
import { registerGrasiasDealViewExtensions } from './extensions/GrasiasDealViewExtension';
import { registerGrasiasWelcomeExtensions } from './extensions/GrasiasWelcomeExtension';
import { registerGrasiasPartnerViewExtensions } from './extensions/GrasiasPartnerViewExtension';
import { CrmExtensions } from '@ayocore/erp/crm/client/extensions';
import { registerCrmCommentsExtensions } from '@ayocore/erp/crm/client/extensions/CrmCommentsExtension';
import { PartnerViewExtensions } from '@ayocore/erp/crm/client/components/Partners/views/PartnerViewExtensions';
import { DealViewExtensions } from '@ayocore/erp/crm/client/components/Deals/views/DealViewExtensions';
import { DealListExtensions } from '@ayocore/erp/crm/client/components/Deals/DealListExtensions';
import { LeadListExtensions } from '@ayocore/erp/crm/client/components/Leads/LeadListExtensions';
import { SupplierInvoiceListExtensions } from '@ayocore/erp/procurement/client/components/SupplierInvoices/SupplierInvoiceListExtensions';
import type { useLeadRepository } from '@ayocore/erp/crm/client/rpc';
import { CRM_DEAL_TYPES } from '@ayocore/erp/crm/constants';
import { CreatePartnerFormGrasias } from './components/CreatePartnerFormGrasias';
import { EditPartnerFormGrasias } from './components/EditPartnerFormGrasias';
import { CreateLeadFormGrasias } from './components/CreateLeadFormGrasias';
// import { PartnerDocuments } from '@ayocore/erp/crm/client/components/Partners/views/components';
import { IconFileSpreadsheet } from '@tabler/icons-react';
import { generateReport } from '@ayocore/platform/reporting/client/rpc';
import { registerNotificationScope } from '@ayocore/platform/notifications/client';
import { CONTRACTOR_DOC_KEY_PREFIX, CONTRACTOR_DOCS_SCOPE } from '../common/notification-keys';
import { ContractorDocsMenuBadge } from './components/ContractorDocsMenuBadge';
import { LinkLeadAction } from './components/LinkLeadAction';
import React from 'react';

/**
 * Регистрация клиентской части модуля grasias.
 */
export async function setup(app: AppRuntime): Promise<void> {
  const grasias = app.useModule(moduleInfo.name, {
    version: moduleInfo.version,
    description: moduleInfo.description,
  });

  // Переопределяем формы партнёра: обязательные поля для Грасиас
  CrmExtensions.Partners.createForm.set(CreatePartnerFormGrasias);
  CrmExtensions.Partners.editForm.set(EditPartnerFormGrasias);
  CrmExtensions.Leads.createForm.set(CreateLeadFormGrasias);

  // В новых счетах из реестра доступны только сегодняшние и будущие даты оплаты.
  SupplierInvoiceListExtensions.disallowPastPaymentDates.register(() => true);
  // Grasias допускает пересмотр счёта на любом этапе; сервер при фактической
  // правке возвращает его в «На оплату», не удаляя уже созданные оплаты.
  SupplierInvoiceListExtensions.allowEditingAfterSend.register(() => true);

  registerGrasiasLeadViewExtensions();
  registerGrasiasDealViewExtensions();
  registerGrasiasWelcomeExtensions();
  registerGrasiasPartnerViewExtensions();

  // Возвращаем вкладку «Комментарии» в карточке партнёра — точечно для Grasias.
  registerCrmCommentsExtensions({ partner: true });

  // Отчёт "Опись документов" в списке сделок.
  // Берём ids (а не rows) — в выделении могут быть строки с других страниц,
  // которые в rows не попали бы.
  DealListExtensions.bulkActions.register(() => ({
    title: 'Опись документов',
    handler: async ({ ids }) => {
      const entityIds = ids.map(Number).filter(n => Number.isFinite(n));
      if (!entityIds.length) return;
      const { url } = await generateReport('crm_opis', { entityIds });
      window.open(url, '_blank');
    },
    icon: React.createElement(IconFileSpreadsheet, { size: 14 }),
  }));

  // Менеджерский отчёт «Анализ по сделкам» (без ДОП1/ДОП2) в списке сделок.
  DealListExtensions.bulkActions.register(() => ({
    title: 'Скачать Отчет',
    handler: async ({ ids }) => {
      const entityIds = ids.map(Number).filter(n => Number.isFinite(n));
      if (!entityIds.length) return;
      const { url } = await generateReport('crm_deal_analysis_manager', { entityIds });
      window.open(url, '_blank');
    },
    icon: React.createElement(IconFileSpreadsheet, { size: 14 }),
  }));

  // Отчёт "Входящие заявки" в списке лидов.
  // Выгружаем через терминальный метод репозитория, а не напрямую
  // generateReport: метод видит фильтры, накопленные таблицей, и отдаёт в
  // отчёт только отобранные заявки. Прямой вызов выгружал всю базу.
  LeadListExtensions.actions.register(() => ({
    title: 'Выгрузить отчёт',
    handler: async ({ getRepository }) => {
      // Сужение до сгенерированного типа клиента: getRepository() типово
      // обезличен (контроллер знает только строку), на рантайме это тот же
      // прокси LeadRepository с фильтрами.
      const repo = getRepository() as ReturnType<typeof useLeadRepository> | null;
      if (!repo) return;
      const { url } = await repo.generateFilteredReport('crm_incoming_leads');
      window.open(url, '_blank');
    },
    icon: React.createElement(IconFileSpreadsheet, { size: 14 }),
  }));

  // Свой отчёт в разделе «Отчёты» CRM: строчка в списке, страница остаётся
  // грасиасовской. Кода CRM это не касается — она читает реестр.
  CrmReportsRegistry.register({
    code: 'grasias_paid_makets',
    label: 'Анализ платных макетов',
    url: '/grasias/contractor-docs/report',
    order: 100,
  });

  // Кнопка «Привязать лид» в шапке карточки сделки.
  // Связь живёт на заявке (crm_leads.deal_id), поэтому кнопка обновляет
  // заявку, а не сделку — подробности в самом компоненте.
  DealViewExtensions.headerActions.register(({ deal, reload }) =>
    React.createElement(LinkLeadAction, { dealId: deal.id, onLinked: reload })
  );

  // Скрываем панель событий в карточках сделок типа SALE (партнёры — с Activity).
  DealViewExtensions.hideEventList.register(
    ({ deal }) => deal.deal_type?.slug === CRM_DEAL_TYPES.SALE
  );

  // На главной карточке партнёра: убираем блок контактных лиц (оставляем только на вкладке)
  PartnerViewExtensions.hideMainPanelBlocks.register(() => ['contactPersons']);
  // Блок документов на главной — выключен (проверка работы hide). Для включения раскомментировать:
  // PartnerViewExtensions.mainPanelBlocks.register(({ partner }) =>
  //   React.createElement(PartnerDocuments, { partnerId: partner.id })
  // );

  // Раздел показывает счётчиком, сколько документов подрядчиков ждут внимания.
  // Свои уведомления область узнаёт по префиксу ключа группы — тому же, по
  // которому их схлопывает серверное правило дедупликации
  registerNotificationScope({
    code: CONTRACTOR_DOCS_SCOPE,
    match: marker => marker.dedup_key?.startsWith(CONTRACTOR_DOC_KEY_PREFIX) ?? false,
  });

  grasias.useNavigation([
    {
      icon: IconFileImport,
      label: 'Таблицы Подрядчиков',
      url: '/grasias/contractor-docs',
      component: lazy(() => import('./components/ContractorDocsList')),
      badge: ContractorDocsMenuBadge,
    },
    {
      // Маршрут свой, а ссылка на него живёт в списке раздела «Отчёты» CRM
      // (регистрация ниже). Отдельным пунктом меню не показываем — иначе один
      // отчёт был бы в двух местах.
      icon: IconChartBar,
      label: 'Анализ платных макетов',
      url: '/grasias/contractor-docs/report',
      component: lazy(() => import('./components/ContractorDocsReport')),
      hiddenFromMenu: true,
    },
    {
      // ВРЕМЕННО: песочница группировки строк (см. GroupingSandbox.tsx).
      // Удалить вместе с компонентом, когда визуал будет выбран.
      icon: IconChartBar,
      label: 'Песочница: группировка',
      url: '/grasias/grouping-sandbox',
      component: lazy(() => import('./components/GroupingSandbox')),
      hiddenFromMenu: true,
    },
    {
      icon: IconFileImport,
      label: 'Детализация документа подрядчика',
      url: '/grasias/contractor-docs/:queryGuid',
      component: lazy(() => import('./components/ContractorDocDetail')),
      hiddenFromMenu: true,
    },
  ]);
}
